/**
 * Post detail (`/post/[id]`) — a single feed post.
 *
 * Reads live `GET /feed/posts/:id`; like via `POST .../react`. Reachable by
 * tapping a live post in News. Dislikes and the comment thread are intentionally
 * hidden (product decision 2026-06-27); the author edit/delete actions stay.
 */
import { useState } from 'react'
import { Image } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { Input, Stack, Text, TextArea, XStack, YStack } from 'tamagui'

import { api } from '../../../src/api/client'

import { useApp } from '../../../src/i18n'
import { useDocumentTitle } from '../../../src/lib/useDocumentTitle'
import {
  usePost,
  useReact,
  useUpdatePost,
  useDeletePost,
} from '../../../src/features/feed/useFeed'
import { useMe } from '../../../src/features/users/useUsers'
import { canModeratePost } from '../../../src/auth/roles'
import { CHANNEL_LABEL, CHANNEL_TONE } from '../../../src/features/feed/channels'
import { timeAgo } from '../../../src/lib/format'
import { Avatar, Button, Card, Enter, Icon, Tag } from '../../../src/ui'
import { Page } from '../../../src/ui/Screen'
import { ReactionButton } from '../../../src/features/feed/ReactionButton'

export default function PostDetailScreen() {
  const { t, l } = useApp()
  const router = useRouter()
  const { id } = useLocalSearchParams<{ id: string }>()
  const postId = String(id ?? '')

  const { data: post, isLoading, isError } = usePost(postId)
  useDocumentTitle(post?.title ?? l('nav.news'))
  const { data: me } = useMe()
  const react = useReact()

  // Edit/delete: author OR a moderator (admin/hr) — mirrors backend assertCanModerate.
  const canModerate = !!post && canModeratePost(me, post.author.id)
  const [editing, setEditing] = useState(false)
  const [eTitle, setETitle] = useState('')
  const [eBody, setEBody] = useState('')
  const [confirmDel, setConfirmDel] = useState(false)
  const update = useUpdatePost(postId, () => setEditing(false))
  const del = useDeletePost(() => router.back())

  const startEdit = () => {
    if (!post) return
    setETitle(post.title)
    setEBody(post.body)
    setEditing(true)
  }
  const canSave = eTitle.trim().length >= 3 && eBody.trim().length >= 1 && !update.isPending
  const saveEdit = () => {
    if (!canSave) return
    update.mutate({ title: eTitle.trim(), body: eBody.trim() })
  }

  const back = (
    <XStack
      onPress={() => router.back()}
      cursor="pointer"
      animation="quick"
      alignItems="center"
      gap={6}
      alignSelf="flex-start"
      paddingVertical={4}
      hoverStyle={{ opacity: 0.8 }}
    >
      <Icon name="arrow-left" size={16} color="$primary" />
      <Text fontSize={13} fontWeight="600" color="$primary">
        {l('post.back')}
      </Text>
    </XStack>
  )

  return (
    <Page maxWidth={720}>
      {back}

      {isLoading ? (
        <Card padding={40} alignItems="center">
          <Text fontSize={13} color="$muted">
            …
          </Text>
        </Card>
      ) : isError || !post ? (
        <Card padding={40} alignItems="center" gap={6}>
          <Text fontSize={14} fontWeight="700" color="$color">
            {l('post.notFound')}
          </Text>
        </Card>
      ) : (
        <>
          {/* Post — content blocks cascade in: header → body → attachments → reactions. */}
          <Card padding={20}>
            <Enter index={0}>
            <XStack alignItems="center" gap={10}>
              <Avatar name={post.author.fullName} seed={post.author.id} />
              <YStack flex={1} minWidth={0}>
                <Text fontSize={14} fontWeight="600" color="$color" numberOfLines={1}>
                  {post.author.fullName}
                </Text>
                <Text fontSize={11} color="$muted" numberOfLines={1}>
                  {post.author.jobTitle ? `${post.author.jobTitle} · ` : ''}
                  {timeAgo(post.createdAt)}
                </Text>
              </YStack>
              {post.isPinned && (
                <XStack alignItems="center" gap={4}>
                  <Icon name="pin" size={13} color="$tertiary" />
                  <Text fontSize={11} fontWeight="600" color="$muted">
                    {l('feed.pinned')}
                  </Text>
                </XStack>
              )}
              <Tag tone={CHANNEL_TONE[post.channel] ?? 'purple'}>
                {t(CHANNEL_LABEL[post.channel] ?? post.channel)}
              </Tag>
            </XStack>
            </Enter>

            <Enter index={1}>
            {editing ? (
              <YStack gap={8} marginTop={14}>
                <Input
                  value={eTitle}
                  onChangeText={setETitle}
                  placeholder={l('feed.titlePlaceholder')}
                  placeholderTextColor="$tertiary"
                  height={40}
                  fontSize={16}
                  fontWeight="700"
                  color="$color"
                  backgroundColor="$bgLayout"
                  borderWidth={1}
                  borderColor="$borderColor"
                  borderRadius={7}
                  paddingHorizontal={11}
                  focusStyle={{ borderColor: '$primary' }}
                />
                <TextArea
                  value={eBody}
                  onChangeText={setEBody}
                  placeholder={l('feed.placeholder')}
                  placeholderTextColor="$tertiary"
                  minHeight={96}
                  fontSize={15}
                  color="$color"
                  backgroundColor="$bgLayout"
                  borderWidth={1}
                  borderColor="$borderColor"
                  borderRadius={7}
                  paddingHorizontal={11}
                  paddingVertical={9}
                  focusStyle={{ borderColor: '$primary' }}
                />
                <XStack gap={8} justifyContent="flex-end">
                  <Button onPress={() => setEditing(false)}>{t({ ru: 'Отмена', en: 'Cancel' })}</Button>
                  <Button variant="primary" onPress={saveEdit} opacity={canSave ? 1 : 0.5} cursor={canSave ? 'pointer' : 'default'}>
                    {update.isPending ? t({ ru: 'Сохраняю…', en: 'Saving…' }) : t({ ru: 'Сохранить', en: 'Save' })}
                  </Button>
                </XStack>
              </YStack>
            ) : (
              <>
                <Text fontSize={20} fontWeight="800" color="$color" lineHeight={26} marginTop={14}>
                  {post.title}
                </Text>
                <Text fontSize={15} color="$color" lineHeight={23} marginTop={8}>
                  {post.body}
                </Text>
                {canModerate && (
                  <XStack gap={8} marginTop={14} alignItems="center" flexWrap="wrap">
                    <Button icon={<Icon name="pencil" size={14} color="$muted" />} onPress={startEdit}>
                      {t({ ru: 'Изменить', en: 'Edit' })}
                    </Button>
                    {confirmDel ? (
                      <>
                        <Button onPress={() => setConfirmDel(false)}>{t({ ru: 'Отмена', en: 'Cancel' })}</Button>
                        <Button
                          variant="primary"
                          onPress={() => del.mutate(post.id)}
                          opacity={del.isPending ? 0.6 : 1}
                          icon={<Icon name="x" size={14} color="#fff" />}
                        >
                          {t({ ru: 'Удалить?', en: 'Delete?' })}
                        </Button>
                      </>
                    ) : (
                      <Button icon={<Icon name="x" size={14} color="$muted" />} onPress={() => setConfirmDel(true)}>
                        {t({ ru: 'Удалить', en: 'Delete' })}
                      </Button>
                    )}
                  </XStack>
                )}
              </>
            )}
            </Enter>

            {!editing && post.attachments && post.attachments.length > 0 && (
              <Enter index={2} gap={10} marginTop={14}>
                {post.attachments.map((a) =>
                  a.mime?.startsWith('image/') ? (
                    <Image
                      key={a.id}
                      source={{ uri: api.fileUrl(a.id) }}
                      style={{ width: '100%', height: 280, borderRadius: 8 }}
                      resizeMode="cover"
                    />
                  ) : (
                    <XStack
                      key={a.id}
                      alignItems="center"
                      gap={8}
                      padding={10}
                      borderRadius={7}
                      borderWidth={1}
                      borderColor="$borderColor"
                      backgroundColor="$bgLayout"
                    >
                      <Icon name="paperclip" size={15} color="$muted" />
                      <Text fontSize={13} color="$color" flex={1} numberOfLines={1}>
                        {a.name}
                      </Text>
                    </XStack>
                  ),
                )}
              </Enter>
            )}

            <Enter index={3}>
            <XStack gap={2} marginTop={16} paddingTop={10} borderTopWidth={1} borderColor="$borderSecondary">
              <ReactionButton icon="heart" count={post.likes} onPress={() => react.mutate({ id: post.id, type: 'LIKE' })} />
            </XStack>
            </Enter>
          </Card>

          <Stack height={8} />
        </>
      )}
    </Page>
  )
}
